import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Zap, Loader2, CheckCircle2, XCircle, Clock, Pause, Play,
  Send, AlertTriangle, Activity, BarChart3
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientPortal = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCR, setShowCR] = useState(false);
  const [crForm, setCrForm] = useState({ agent_id: '', title: '', description: '', priority: 'normal' });
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!token) { setError('No access token'); setLoading(false); return; }
    fetchData();
    fetchUsage();
  }, [token]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/portal/view?token=${token}`);
      setData(res.data);
    } catch { setError('Invalid or expired access token'); }
    finally { setLoading(false); }
  };

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/portal/view/usage?token=${token}`);
      setUsage(res.data);
    } catch { /* ignore */ }
  };

  const handlePause = async (agentId) => {
    try {
      await axios.post(`${API}/portal/view/agent/${agentId}/pause?token=${token}`);
      toast.success('Agent paused');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleResume = async (agentId) => {
    try {
      await axios.post(`${API}/portal/view/agent/${agentId}/resume?token=${token}`);
      toast.success('Agent resumed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSubmitCR = async () => {
    if (!crForm.agent_id || !crForm.title || !crForm.description) return toast.error('Fill all fields');
    try {
      await axios.post(`${API}/portal/view/change-request?token=${token}`, crForm);
      toast.success('Change request submitted');
      setShowCR(false);
      setCrForm({ agent_id: '', title: '', description: '', priority: 'normal' });
      fetchData();
    } catch { toast.error('Failed to submit'); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center text-center">
      <div><AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="text-lg font-bold mb-1">Access Denied</p><p className="text-muted-foreground text-sm">{error}</p></div>
    </div>
  );

  const branding = data?.branding || {};
  const brandColor = branding.primary_color || '#10b981';
  const brandName = branding.company_name || data?.operator_name || 'HumanScaled';

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="noise-overlay" />
      <nav className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={brandName} className="h-7" />
            ) : (
              <div className="w-7 h-7 flex items-center justify-center" style={{ backgroundColor: brandColor }}><Zap className="w-4 h-4 text-black" /></div>
            )}
            <span className="font-black text-base tracking-tight">{brandName}</span>
            <span className="text-xs text-muted-foreground ml-2">Client Portal</span>
          </div>
          <span className="text-sm text-muted-foreground">{data?.client_name}</span>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="portal-title">Welcome, {data?.client_name}</h1>
            <p className="text-sm text-muted-foreground">Managed by {data?.operator_name}</p>
          </div>
          <Button onClick={() => setShowCR(true)} variant="outline" className="border-white/10 rounded-sm" data-testid="submit-cr-btn">
            <Send className="w-4 h-4 mr-1" /> Request Change
          </Button>
        </div>

        {/* Agents */}
        <div data-testid="portal-agents">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Active Agents</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {(data?.agents || []).map(agent => (
              <div key={agent.id} className="bg-[#0a0a0a] border border-white/5 p-5" data-testid={`portal-agent-${agent.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-sm">{agent.name}</h3>
                    <span className="text-[10px] text-muted-foreground font-mono">v{agent.blueprint_version}</span>
                  </div>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${
                    agent.status === 'running' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                    agent.status === 'paused' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                    'bg-white/10 text-muted-foreground border-white/10'
                  }`}>{agent.status?.toUpperCase()}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center mb-3">
                  <div><p className="text-[10px] text-muted-foreground">Runs</p><p className="font-mono text-sm">{agent.metrics?.total_runs || 0}</p></div>
                  <div><p className="text-[10px] text-muted-foreground">Success</p><p className="font-mono text-sm">{agent.metrics?.total_runs > 0 ? Math.round((agent.metrics.successful_runs / agent.metrics.total_runs) * 100) : 0}%</p></div>
                  <div><p className="text-[10px] text-muted-foreground">Health</p><p className={`font-mono text-sm ${agent.health_status === 'healthy' ? 'text-emerald-400' : 'text-amber-400'}`}>{agent.health_status}</p></div>
                </div>
                {agent.last_run && <p className="text-[10px] text-muted-foreground">Last run: {new Date(agent.last_run.started_at).toLocaleString()} ({agent.last_run.status})</p>}
                <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                  {agent.status === 'running' ? (
                    <Button onClick={() => handlePause(agent.id)} variant="outline" size="sm" className="border-amber-500/20 text-amber-400 rounded-sm h-7 text-xs flex-1"><Pause className="w-3 h-3 mr-1" /> Pause</Button>
                  ) : agent.status === 'paused' ? (
                    <Button onClick={() => handleResume(agent.id)} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-7 text-xs flex-1"><Play className="w-3 h-3 mr-1" /> Resume</Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Usage */}
        {usage?.usage && Object.keys(usage.usage).length > 0 && (
          <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="portal-usage">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Usage Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(usage.usage).map(([provider, stats]) => (
                <div key={provider} className="text-center">
                  <p className="text-[10px] text-muted-foreground uppercase">{provider}</p>
                  <p className="font-mono text-lg font-bold text-white">{stats.units}</p>
                  <p className="text-[10px] text-muted-foreground">{stats.calls} calls · ${stats.cost}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Change Requests */}
        {data?.change_requests?.length > 0 && (
          <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="portal-crs">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Change Requests</h2>
            <div className="space-y-2">
              {data.change_requests.map(cr => (
                <div key={cr.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{cr.title}</p>
                    <p className="text-xs text-muted-foreground">{cr.description.slice(0, 60)}</p>
                  </div>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 ${
                    cr.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                    cr.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                    cr.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>{cr.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Change Request Modal */}
      <Dialog open={showCR} onOpenChange={setShowCR}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Submit Change Request</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Agent</label>
              <select value={crForm.agent_id} onChange={e => setCrForm({ ...crForm, agent_id: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-sm p-2 text-sm text-white" data-testid="cr-agent-select">
                <option value="">Select agent</option>
                {(data?.agents || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Title</label>
              <Input value={crForm.title} onChange={e => setCrForm({ ...crForm, title: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="What needs to change?" data-testid="cr-title" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Description</label>
              <Textarea value={crForm.description} onChange={e => setCrForm({ ...crForm, description: e.target.value })} className="bg-black/50 border-white/10 rounded-sm min-h-[80px]" placeholder="Describe the change..." data-testid="cr-description" />
            </div>
            <Button onClick={handleSubmitCR} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="cr-submit">Submit Request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortal;
